import React, { useState, useEffect, useCallback } from "react";

import SubHeader from "components/common/SubHeader";
import ThumbnailCarousel from "components/shopping/ThumbnailCarousel";
import { useParams, useNavigate } from "react-router-dom";
import TabHeader from "components/common/TabHeader";
import TuiViewer from "components/common/TuiViewer";
import { getProduct } from "api/store";
import { postAddToCart } from "api/cart";
import { getProductReviews } from "api/review";
import ProductPolicyTab from "components/store/ProductPolicyTab";
import { useSnackbar } from "contexts/SnackbarContext";
import {
    trackViewItem,
    trackAddToCart as trackAddToCartGA,
} from "utils/analytics";

import cartIcon from "assets/images/icons/cart_icon.svg";
import ReviewList from "components/review/ReviewList";
import ErrorPage from "pages/error/ErrorPage";
import ProductInfo from "components/store/ProductInfo";
import Seo from "components/common/Seo";

const ProductDetail = () => {
    const navigate = useNavigate();
    const { productId } = useParams();

    const { showError, showSuccess } = useSnackbar();

    const [product, setProduct] = useState({});
    const [mainImages, setMainImages] = useState([]);
    const [purchaseCnt, setPurchaseCnt] = useState(1);
    const [selectedTab, setSelectedTab] = useState("상세정보");
    const [selectedOption, setSelectedOption] = useState(null);
    // 어드민에서 설정한 "사용자 입력 옵션" 질문별 답변. 질문 인덱스와 동일 순서로 보관한다.
    const [customerInputs, setCustomerInputs] = useState([]);
    const [errorStatus, setErrorStatus] = useState(null);
    const [reviewSummary, setReviewSummary] = useState(null);
    const [reviews, setReviews] = useState([]);
    const [reviewHasMore, setReviewHasMore] = useState(false);
    const [reviewLoading, setReviewLoading] = useState(false);

    // 리뷰 데이터는 페이지 로드 시점에 불러와서 캐싱한다.
    const fetchReviews = useCallback(async () => {
        if (!productId) return;
        setReviewLoading(true);
        try {
            const data = await getProductReviews("products", productId);
            const results = data.results || data || [];
            setReviews(results);
            setReviewSummary(data.summary || null);
            setReviewHasMore(!!data.next);
        } catch {
            // silent
        } finally {
            setReviewLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const reviewCount = reviewSummary?.total_count ?? reviews.length;
    const reviewTabLabel = `리뷰(${reviewCount})`;
    const menus = ["상세정보", reviewTabLabel, "배송/반품 정보"];

    // 리뷰 개수가 바뀌면 활성 탭 라벨도 동기화한다.
    useEffect(() => {
        if (selectedTab?.startsWith("리뷰") && selectedTab !== reviewTabLabel) {
            setSelectedTab(reviewTabLabel);
        }
    }, [reviewTabLabel, selectedTab]);


    // 상품이 로드되면 사용자 입력 옵션 개수에 맞춰 답변 슬롯을 초기화한다(기존 입력값 보존).
    const customerInputOptions = Array.isArray(product?.customer_input_options)
        ? product.customer_input_options
        : [];

    useEffect(() => {
        const opts = product?.customer_input_options;
        if (!Array.isArray(opts) || opts.length === 0) return;
        setCustomerInputs((prev) => opts.map((_, i) => prev[i] ?? ""));
    }, [product?.customer_input_options]);

    const handleCustomerInputChange = (index, value) => {
        setCustomerInputs((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    // 모든 사용자 입력 옵션이 채워져야 구매하기가 활성화된다.
    const isCustomerInputsFilled =
        customerInputOptions.length === 0 ||
        customerInputOptions.every(
            (_, i) => (customerInputs[i] ?? "").trim().length > 0
        );

    useEffect(() => {
        getProduct(productId)
            .then((res) => {
                setProduct(res);
                if (res.images && Array.isArray(res.images)) {
                    setMainImages(res.images.map((image) => image.image));
                }

                // GA4: 상품 조회 이벤트 추적
                trackViewItem({
                    item_id: res.id,
                    item_name: res.name,
                    item_category: "product",
                    price: res.discount_price || res.price,
                    brand: res.brand || "",
                });
            })
            .catch((error) => {
                setErrorStatus(error.response?.status === 404 ? 404 : 500);
            });
    }, [productId]);

    if (errorStatus) {
        return (
            <ErrorPage
                status={errorStatus}
                onRetry={errorStatus === 500 ? () => { setErrorStatus(null); setProduct({}); } : undefined}
            />
        );
    }

    const TabContent = () => {
        const detailImages =
            product?.images?.filter((image) => image.image_type === "detail") ||
            [];
        if (selectedTab?.startsWith("리뷰")) {
            return (
                <ReviewList
                    summary={reviewSummary}
                    reviews={reviews}
                    hasMore={reviewHasMore}
                    loading={reviewLoading}
                    onLoadMore={fetchReviews}
                />
            );
        }
        switch (selectedTab) {
            case "상세정보":
                return <TuiViewer htmlString={product.detailed_content} />;
            case "배송/반품 정보":
                return (
                    <ProductPolicyTab
                        shipping_policy={product.shipping_policy}
                        return_exchange_policy={product.return_exchange_policy}
                    />
                );
            default:
                return (
                    <TuiViewer
                        className="class-detail-wrap"
                        images={detailImages.map((image) => image.image)}
                    />
                );
        }
    };

    const handleSelectOption = (selectedOptions) => {
        setSelectedOption(selectedOptions);
    };

    function findMatchedSku(selected, skuList) {
        if (selected === null) return null;

        const selectedValueIds = new Set(
            Object.values(selected).map((v) => v.valueId)
        );

        return skuList.find((sku) => {
            const skuValueIds = sku.option_values.map((v) => v.id);
            if (skuValueIds.length !== selectedValueIds.size) return false;

            //skuValueIds에 선택한 valueId가 모두 포함되어 있다면 true를 반환
            return skuValueIds.every((id) => selectedValueIds.has(id));
        });
    }

    return (
        <div className="page-product-detail">
            <Seo
                title={product?.name}
                description={product?.name ? `${product.name}을(를) 버킷리스트클래스 스토어에서 구매하세요.` : undefined}
                image={mainImages?.[0]?.image || mainImages?.[0]}
            />
            <SubHeader
                title={product?.name || ""}
                // menus={[{
                //     name: '공유',
                //     icon: shareIcon,
                //     callback: () => share({ title: product?.name || '', appPath: `/product/detail/${productId}` }),
                // }]}
                menus={[
                  {
                      name: "장바구니",
                      icon: cartIcon,
                      callback: () => navigate("/cart"),
                  },
              ]}
            />
            {product && (
                <div className="product-detail-header">
                    <ThumbnailCarousel images={mainImages} height={390} />
                    <ProductInfo
                        type="product"
                        product={{
                            title: product.name,
                            discount_price: product.discount_price,
                            price: product.price,
                            discount_rate: product.discount_rate,
                            currency: product.currency,
                            images: product.images,
                            id: productId,
                            free_threshold:
                                product.shipping_policy?.free_threshold,
                        }}
                        options={product.product_options}
                        handleSelectOption={handleSelectOption}
                        selectedOptionId={selectedOption}
                        purchaseCnt={purchaseCnt}
                        setPurchaseCnt={setPurchaseCnt}
                        hasShareIcon={true}
                    />
                    {customerInputOptions.length > 0 && (
                        <div className="customer-input-options">
                            {customerInputOptions.map((question, index) => (
                                <div
                                    className="customer-input-option"
                                    key={index}
                                >
                                    <label
                                        className="customer-input-label"
                                        htmlFor={`customer-input-${index}`}
                                    >
                                        {question}
                                    </label>
                                    <input
                                        id={`customer-input-${index}`}
                                        type="text"
                                        className="customer-input-field"
                                        value={customerInputs[index] ?? ""}
                                        onChange={(e) =>
                                            handleCustomerInputChange(
                                                index,
                                                e.target.value
                                            )
                                        }
                                        placeholder="입력해주세요"
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <div
                        className="btn-wrap-flex gap-8"
                        style={{
                            padding: "0 20px",
                            marginBottom: "25px",
                        }}
                    >
                        <button
                            className={`btn btn-outline-purple500 btn-large`}
                            onClick={() => {
                                if (
                                    product.product_options.length > 0 &&
                                    selectedOption === null
                                ) {
                                    showError("옵션을 선택해주세요.");
                                    return;
                                }
                                if (
                                    selectedOption !== null &&
                                    Object.keys(selectedOption).length !==
                                        product.product_options.length
                                ) {
                                    showError("옵션을 모두 선택해주세요.");
                                    return;
                                }
                                const matchedSku = findMatchedSku(
                                    selectedOption,
                                    product.variants
                                );

                                postAddToCart({
                                    item_type: "product",
                                    item_id: Number(productId),
                                    quantity: purchaseCnt,

                                    variant_id: matchedSku?.id || null,
                                })
                                    .then((res) => {
                                        // GA4: 장바구니 추가 이벤트 추적
                                        trackAddToCartGA({
                                            item_id: productId,
                                            item_name: product.name,
                                            item_category: "product",
                                            price:
                                                matchedSku?.final_price ||
                                                product.discount_price ||
                                                product.price,
                                            quantity: purchaseCnt,
                                            variant: matchedSku?.sku || "",
                                        });

                                        showSuccess(
                                            "장바구니에 추가되었습니다.",
                                            {
                                                buttonText: "장바구니 이동",
                                                onButtonClick: () => {
                                                    navigate("/cart");
                                                },
                                                duration: 3000,
                                                bottomPosition: 20,
                                            }
                                        );
                                    })
                                    .catch((err) => {
                                        if (err.status === 500) {
                                            showError(
                                                "장바구니 추가 실패했습니다. 관리자에게 문의해주세요."
                                            );
                                        } else {
                                            showError(
                                                err.response.data.message ||
                                                    "다시 시도해주세요."
                                            );
                                        }

                                        return;
                                    });
                            }}
                        >
                            장바구니
                        </button>
                        <button
                            className={`btn btn-large ${
                                isCustomerInputsFilled
                                    ? "btn-purple500"
                                    : "btn-disabled"
                            }`}
                            disabled={!isCustomerInputsFilled}
                            onClick={() => {
                                if (
                                    product.product_options.length > 0 &&
                                    selectedOption === null
                                ) {
                                    showError("옵션을 선택해주세요.");
                                    return;
                                }
                                if (
                                    selectedOption !== null &&
                                    Object.keys(selectedOption).length !==
                                        product.product_options.length
                                ) {
                                    showError("옵션을 모두 선택해주세요.");
                                    return;
                                }
                                if (!isCustomerInputsFilled) {
                                    showError("추가 입력 정보를 모두 입력해주세요.");
                                    return;
                                }
                                // 결제 페이지에서 표시·주문 전송할 답변을 verbatim으로 넘긴다.
                                const navState =
                                    customerInputOptions.length > 0
                                        ? { state: { customerInputs } }
                                        : undefined;
                                const matchedSku = findMatchedSku(
                                    selectedOption,
                                    product.variants
                                );
                                if (matchedSku === null) {
                                    navigate(
                                        `/payment/product/${productId}?purchaseCnt=${purchaseCnt}`,
                                        navState
                                    );
                                    return;
                                }
                                if (matchedSku?.stock_quantity > 0) {
                                    navigate(
                                        `/payment/product/${productId}?purchaseCnt=${purchaseCnt}&sku=${matchedSku.sku}&id=${matchedSku.id}`,
                                        navState
                                    );
                                    return;
                                } else {
                                    showError(
                                        "재고가 없습니다. 다른 옵션으로 선택해주세요"
                                    );
                                    return;
                                }
                            }}
                        >
                            구매하기
                        </button>
                    </div>
                </div>
            )}
            <TabHeader
                menus={menus}
                defaultTab={selectedTab}
                setSelectedTab={setSelectedTab}
            />
            <div className="tab-content-wrap">
                <TabContent />
            </div>
        </div>
    );
};

export default ProductDetail;
